import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Activity, RefreshCw, Maximize2, Minimize2, ZoomOut, Loader2,
  CheckCircle2, XCircle, Search, Radio,
} from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import TimeRangePicker, { makeRolling, resolveWindow, rangeSpanSec } from '@/components/TimeRangePicker';

const DAY_MS = 86400 * 1000;

// Distinct, high-contrast line colours (dark-theme friendly).
const PALETTE = [
  '#38bdf8', '#34d399', '#f59e0b', '#f472b6', '#a78bfa',
  '#f87171', '#22d3ee', '#facc15', '#4ade80', '#fb923c',
  '#60a5fa', '#e879f9', '#2dd4bf', '#fca5a5', '#c084fc',
];

function fmtClock(ms) {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
function fmtFull(ms) {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
function fmtDay(ms) {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function MRTGLatency() {
  const { toast } = useToast();
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState([]); // instances
  const [timeRange, setTimeRange] = useState(makeRolling(900, 'Last 15 minutes'));
  const [search, setSearch] = useState('');
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [meta, setMeta] = useState(null); // {step_seconds, scrape_interval_seconds, native_resolution}
  const [series, setSeries] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [live, setLive] = useState(false);

  // ---- Load device list (from Prometheus) ----
  const loadDevices = useCallback(async () => {
    try {
      const { data } = await api.get('/network/mrtg/devices');
      setDevices(data.devices || []);
      // Sengaja TIDAK auto-select device apa pun. User awam akan disambut
      // empty state yang mengarahkan untuk memilih device terlebih dahulu.
    } catch (e) {
      toast({ title: 'Gagal memuat daftar device', description: formatApiError(e), variant: 'destructive' });
    } finally {
      setLoadingDevices(false);
    }
  }, [toast]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  // ---- Load latency data ----
  const loadLatency = useCallback(async (silent = false) => {
    if (!selected.length) { setSeries([]); return; }
    if (!silent) setLoadingChart(true);
    try {
      const [start, end] = resolveWindow(timeRange);
      const { data } = await api.get('/network/mrtg/latency', {
        params: { start, end, instances: selected.join(',') },
      });
      setSeries(data.series || []);
      setMeta({
        step_seconds: data.step_seconds,
        scrape_interval_seconds: data.scrape_interval_seconds,
        native_resolution: data.native_resolution,
        start_ms: data.start_ms, end_ms: data.end_ms,
      });
    } catch (e) {
      if (!silent) toast({ title: 'Gagal memuat grafik latency', description: formatApiError(e), variant: 'destructive' });
    } finally {
      if (!silent) setLoadingChart(false);
    }
  }, [selected, timeRange, toast]);

  useEffect(() => { loadLatency(); }, [loadLatency]);

  // ---- Live auto-refresh (rolling ranges only) ----
  useEffect(() => {
    if (!live || !timeRange.rolling) return undefined;
    const intervalMs = rangeSpanSec(timeRange) <= 3600 ? 5000 : 30000;
    const id = setInterval(() => loadLatency(true), intervalMs);
    return () => clearInterval(id);
  }, [live, timeRange, loadLatency]);

  const toggleDevice = (instance) => {
    setSelected((prev) =>
      prev.includes(instance) ? prev.filter((i) => i !== instance) : [...prev, instance]);
  };

  const filteredDevices = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) =>
      (d.device || '').toLowerCase().includes(q) || (d.instance || '').includes(q));
  }, [devices, search]);

  // ---- ECharts option ----
  const option = useMemo(() => {
    const echSeries = series.map((s, idx) => {
      const color = PALETTE[idx % PALETTE.length];
      const lossSet = new Set(s.loss_points || []);
      return {
        name: s.device,
        type: 'line',
        showSymbol: false,
        symbolSize: 6,
        sampling: 'lttb',
        connectNulls: false,
        lineStyle: { width: 1.5, color },
        itemStyle: { color },
        emphasis: { focus: 'series' },
        // Mark real timeout timestamps on the axis.
        markPoint: (s.loss_points && s.loss_points.length) ? {
          symbol: 'pin', symbolSize: 26,
          itemStyle: { color: '#ef4444' },
          data: s.loss_points.slice(0, 200).map((t) => ({
            name: 'timeout', xAxis: t, yAxis: 0,
            value: 'TO',
          })),
          label: { show: false },
        } : undefined,
        data: s.points,
        _loss: lossSet,
      };
    });

    return {
      backgroundColor: 'transparent',
      animation: false,
      grid: { left: 56, right: 24, top: 16, bottom: 72 },
      textStyle: { color: '#94a3b8' },
      legend: {
        type: 'scroll',
        top: 0,
        right: 40,
        left: 0,
        textStyle: { color: '#cbd5e1', fontSize: 11 },
        inactiveColor: '#475569',
        data: echSeries.map((s) => s.name),
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15,23,42,0.95)',
        borderColor: '#334155',
        textStyle: { color: '#e2e8f0', fontSize: 12 },
        axisPointer: { type: 'cross', crossStyle: { color: '#64748b' }, lineStyle: { color: '#64748b' } },
        formatter: (params) => {
          if (!params || !params.length) return '';
          const ts = params[0].axisValue;
          let html = `<div style="font-weight:600;margin-bottom:4px">${fmtFull(ts)}</div>`;
          params.forEach((p) => {
            const val = p.data && p.data[1] != null ? `${Number(p.data[1]).toFixed(3)} ms` : '<span style="color:#ef4444">timeout</span>';
            html += `<div style="display:flex;align-items:center;gap:6px">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
              <span style="flex:1">${p.seriesName}</span>
              <span style="font-weight:600">${val}</span></div>`;
          });
          return html;
        },
      },
      xAxis: {
        type: 'time',
        axisLine: { lineStyle: { color: '#334155' } },
        axisLabel: {
          color: '#94a3b8', fontSize: 11, hideOverlap: true,
          formatter: (val) => fmtClock(val),
        },
        splitLine: { show: false },
        axisPointer: { label: { formatter: (p) => fmtFull(p.value) } },
      },
      yAxis: {
        type: 'value',
        name: 'ms',
        nameTextStyle: { color: '#64748b' },
        axisLabel: { color: '#94a3b8', fontSize: 11 },
        splitLine: { lineStyle: { color: 'rgba(51,65,85,0.4)' } },
        min: 0,
      },
      dataZoom: [
        { type: 'inside', filterMode: 'none', zoomOnMouseWheel: true, moveOnMouseMove: true, moveOnMouseWheel: false },
        { type: 'slider', height: 22, bottom: 20, backgroundColor: 'rgba(30,41,59,0.5)',
          fillerColor: 'rgba(56,189,248,0.15)', borderColor: '#334155',
          textStyle: { color: '#94a3b8', fontSize: 10 },
          labelFormatter: (val) => fmtClock(val) },
      ],
      series: echSeries,
    };
  }, [series]);

  const resetZoom = () => {
    const inst = chartRef.current?.getEchartsInstance();
    inst?.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.().then(() => setFullscreen(true)).catch(() => setFullscreen(true));
    } else {
      document.exitFullscreen?.().then(() => setFullscreen(false)).catch(() => setFullscreen(false));
    }
  };
  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const upCount = devices.filter((d) => d.up).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-display font-bold tracking-tight">MRTG Latency</h1>
            <p className="text-sm text-muted-foreground">
              ICMP latency monitoring · Prometheus ·{' '}
              <span className="text-emerald-400 font-medium">{upCount} up</span> / {devices.length} device
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { loadDevices(); loadLatency(); }}>
            <RefreshCw className={cn('w-4 h-4', loadingChart && 'animate-spin')} />
            <span className="ml-1.5 hidden sm:inline">Refresh</span>
          </Button>
          <Button
            variant={live ? 'default' : 'outline'} size="sm"
            onClick={() => setLive((v) => !v)}
            title="Auto-refresh"
          >
            <Radio className={cn('w-4 h-4', live && 'animate-pulse')} />
            <span className="ml-1.5 hidden sm:inline">{live ? 'Live' : 'Live off'}</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Device selector */}
        <div className="lg:col-span-1 rounded-xl border border-border bg-card overflow-hidden flex flex-col">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cari device / IP…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>{selected.length} dipilih</span>
              <button className="hover:text-foreground" onClick={() => setSelected([])}>Clear</button>
            </div>
          </div>
          <ScrollArea className="flex-1 max-h-[520px]">
            <div className="p-2 space-y-1">
              {loadingDevices ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : filteredDevices.map((d, idx) => {
                const isSel = selected.includes(d.instance);
                const selIdx = selected.indexOf(d.instance);
                const color = isSel ? PALETTE[selIdx % PALETTE.length] : undefined;
                return (
                  <button
                    key={d.instance}
                    onClick={() => toggleDevice(d.instance)}
                    className={cn(
                      'w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                      isSel ? 'bg-primary/10' : 'hover:bg-accent'
                    )}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0 border border-border"
                      style={{ background: color || 'transparent' }}
                    />
                    {d.up
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-medium text-foreground">{d.device}</span>
                      <span className="block truncate text-[11px] text-muted-foreground tabular-nums">{d.instance}</span>
                    </span>
                    {d.latency_ms != null && (
                      <span className="text-[11px] tabular-nums text-muted-foreground">{d.latency_ms.toFixed(1)}ms</span>
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Chart panel */}
        <div ref={containerRef} className="lg:col-span-3 rounded-xl border border-border bg-card flex flex-col">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-border">
            <div className="flex flex-wrap items-center gap-1">
              {RANGES.map((rg) => (
                <button
                  key={rg.key}
                  onClick={() => setRange(rg.key)}
                  className={cn(
                    'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                    range === rg.key
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  {rg.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {meta && (
                <Badge variant="outline" className="text-[10px] font-normal gap-1">
                  <span className="text-muted-foreground">step</span>
                  <span className="tabular-nums text-foreground">{meta.step_seconds}s</span>
                  <span className={cn('ml-1', meta.native_resolution ? 'text-emerald-400' : 'text-amber-400')}>
                    {meta.native_resolution ? '1s native' : `scrape ${meta.scrape_interval_seconds}s`}
                  </span>
                </Badge>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={resetZoom} title="Reset zoom">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleFullscreen} title="Fullscreen">
                {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Chart */}
          <div className={cn('relative flex-1 p-2', fullscreen ? 'bg-background' : '')}>
            {loadingChart && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-sm">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            )}
            {selected.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[420px] text-muted-foreground text-center px-6">
                <Activity className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm font-medium text-foreground">Belum ada device dipilih</p>
                <p className="text-xs mt-1 max-w-sm">
                  Pilih satu atau beberapa device pada panel <span className="text-foreground font-medium">Devices</span> di sebelah kiri untuk mulai menampilkan grafik latency.
                </p>
              </div>
            ) : (
              <ReactECharts
                ref={chartRef}
                option={option}
                notMerge={false}
                lazyUpdate
                style={{ height: fullscreen ? 'calc(100vh - 120px)' : '440px', width: '100%' }}
              />
            )}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground px-1">
        Latency = <code className="text-foreground">probe_icmp_duration_seconds{'{phase="rtt"}'}</code> (dikonversi ke ms).
        Timeout / packet loss ditandai pin merah pada timestamp aktual (<code className="text-foreground">probe_success == 0</code>).
        Range 5m/15m/1h menampilkan data 1 detik penuh tanpa downsampling saat sample 1s tersedia.
      </p>
    </div>
  );
}
